/**
 * MENU BACKGROUND MUSIC — a single looping track playing while the arena
 * menu is on screen, silent the instant a match starts.
 *
 * Owner (2026-08-14): "play this audio with some adjustments in the menu
 * section please". The "adjustments": a fade in/out rather than a hard cut
 * (the track stopping mid-bar the moment you press Kick Off reads as a bug,
 * not a scene change), and a dedicated volume slider (musicVolume, layered
 * under masterVolume — see settings.ts) so a player who wants it quiet or
 * off is not stuck at whatever level shipped.
 *
 * Follows the SAME AudioContext/decodeAudioData idiom commentary/clips.ts and
 * game/audio.ts already use — no `<audio>` tag, no second audio approach in
 * this codebase. Looping via AudioBufferSourceNode.loop rather than an
 * `<audio>` element's own loop attribute, which reliably clicks/gaps at the
 * seam in most browsers; a decoded buffer loops sample-accurately.
 */

const TRACK_URL = '/audio/menu-music.mp3';
const FADE_IN_S = 1.6;
const FADE_OUT_S = 0.9;

export interface MenuMusic {
  /** Final gain = musicVolume * masterVolume — call whenever EITHER changes
   *  (settingsScreen.ts's live.music hook does, from both sliders). */
  setVolume(musicVolume: number, masterVolume: number): void;
  /** Fades out and stops. Safe to call before the track has even started
   *  (e.g. dismounting the menu before the first user gesture unlocked
   *  autoplay) — it just cancels the pending start. */
  dispose(): void;
}

// One AudioContext, one decoded buffer, reused across every menu visit for
// the life of the tab — re-fetching and re-decoding a 2 MB track every time
// the player backs out of a match would be wasteful and would reintroduce a
// beat of silence on every single return to the menu.
let sharedCtx: AudioContext | null = null;
let sharedBuffer: AudioBuffer | null = null;
let bufferPromise: Promise<AudioBuffer> | null = null;

function getCtx(): AudioContext {
  if (!sharedCtx) sharedCtx = new AudioContext();
  return sharedCtx;
}

function loadBuffer(ctx: AudioContext): Promise<AudioBuffer> {
  if (sharedBuffer) return Promise.resolve(sharedBuffer);
  if (!bufferPromise) {
    bufferPromise = fetch(TRACK_URL)
      .then((r) => r.arrayBuffer())
      .then((data) => ctx.decodeAudioData(data))
      .then((buf) => {
        sharedBuffer = buf;
        return buf;
      });
  }
  return bufferPromise;
}

export function mountMenuMusic(musicVolume: number, masterVolume: number): MenuMusic {
  const ctx = getCtx();
  const gain = ctx.createGain();
  gain.gain.value = 0; // ramped up once the buffer is decoded and playing
  gain.connect(ctx.destination);

  let target = musicVolume * masterVolume;
  let source: AudioBufferSourceNode | null = null;
  let disposed = false;

  const begin = async (): Promise<void> => {
    // Autoplay policy: a fresh AudioContext starts 'suspended' until a user
    // gesture. resume() before the gesture has happened is a silent no-op,
    // not an error — the pointerdown/keydown listeners below are what
    // actually unblock it, so this call is safe to attempt unconditionally.
    if (ctx.state === 'suspended') await ctx.resume().catch(() => undefined);
    if (disposed) return;
    const buffer = await loadBuffer(ctx);
    if (disposed) return;
    source = ctx.createBufferSource();
    source.buffer = buffer;
    source.loop = true;
    source.connect(gain);
    source.start();
    const now = ctx.currentTime;
    gain.gain.cancelScheduledValues(now);
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(target, now + FADE_IN_S);
  };

  let onFirstGesture: (() => void) | null = null;
  if (ctx.state === 'suspended') {
    onFirstGesture = (): void => {
      window.removeEventListener('pointerdown', onFirstGesture as () => void);
      window.removeEventListener('keydown', onFirstGesture as () => void);
      void begin();
    };
    window.addEventListener('pointerdown', onFirstGesture, { once: true });
    window.addEventListener('keydown', onFirstGesture, { once: true });
  } else {
    void begin();
  }

  return {
    setVolume(newMusicVolume, newMasterVolume) {
      target = newMusicVolume * newMasterVolume;
      if (!source) return; // not playing yet — `target` is read when it starts
      const now = ctx.currentTime;
      gain.gain.cancelScheduledValues(now);
      gain.gain.setTargetAtTime(target, now, 0.05);
    },
    dispose() {
      if (disposed) return;
      disposed = true;
      if (onFirstGesture) {
        window.removeEventListener('pointerdown', onFirstGesture);
        window.removeEventListener('keydown', onFirstGesture);
      }
      if (!source) return; // never actually started — nothing to fade
      const now = ctx.currentTime;
      const from = gain.gain.value;
      gain.gain.cancelScheduledValues(now);
      gain.gain.setValueAtTime(from, now);
      gain.gain.linearRampToValueAtTime(0, now + FADE_OUT_S);
      const src = source;
      const g = gain;
      setTimeout(() => {
        try {
          src.stop();
        } catch {
          // Already stopped (e.g. the tab backgrounded and the context
          // auto-suspended) — nothing left to do.
        }
        src.disconnect();
        g.disconnect();
      }, FADE_OUT_S * 1000 + 50);
    },
  };
}
